import type { ReactElement } from 'react'
import { icons } from './icons'
import type { IconName } from './names'
import { toEm } from './sizes'
import type { GlyphProps } from './types'

const designBox = { viewBox: '0 0 256 256', width: 1 }
// Font Awesome's chevrons were 0.625em wide (320 of 512) and sat flush; centred in a square they drift inward.
const caretBox = { viewBox: '48 0 160 256', width: 160 / 256 }
// Font Awesome's sort glyph filled its height; Phosphor's stacked carets use 96 of 256, so crop to them.
const sortBox = { viewBox: '72 24 112 208', width: 112 / 208 }
const boxes: Partial<Record<IconName, typeof designBox>> = {
  'angle-right': caretBox,
  'chevron-left': caretBox,
  'chevron-right': caretBox,
  sort: sortBox,
  'sort-up': sortBox,
  'sort-down': sortBox
}
// For glyphs that are only strokes, Phosphor's fill weight draws a filled square or pill with the
// glyph punched out; Font Awesome's solid was the same glyph, heavier. Bold is the honest match.
const strokeOnly: ReadonlySet<IconName> = new Set([
  'bars',
  'check',
  'close',
  'exclamation',
  'grip-dots-vertical',
  'grip-vertical',
  'infinity',
  'link',
  'list',
  'minus',
  'plus',
  'times',
  'unlink',
  'xmark'
])

export function renderGlyph({
  name,
  variant,
  size,
  title,
  flip,
  color,
  style,
  ...rest
}: GlyphProps): ReactElement | null {
  const Component = icons[name]
  // names come from stored data too (user role icons), so an unknown one must not take the page down
  if (!Component) return null
  const box = boxes[name] ?? designBox
  const labelled = title || rest['aria-label'] || rest['aria-labelledby'] || rest.role
  return (
    <Component
      {...rest}
      // Phosphor's regular stroke reads lighter than the Font Awesome regular it replaced
      weight={variant === 'solid' && !strokeOnly.has(name) ? 'fill' : 'bold'}
      size={toEm(size)}
      width={rest.width ?? toEm(size, box.width)}
      viewBox={box.viewBox}
      // apps set `svg { fill: currentColor }` globally, which beats Phosphor's fill attribute
      style={color ? { color, ...style } : style}
      alt={title}
      mirrored={flip}
      aria-hidden={labelled ? undefined : true}
      data-icon={name}
    />
  )
}
